import { useNavigate } from "react-router-dom";
import { useVehicleSim } from "@/hooks/useVehicleSim";
import WelcomeScreen from "@/components/vehicle/WelcomeScreen";
import VehicleMarketplace from "@/components/vehicle/VehicleMarketplace";
import VehicleDetail from "@/components/vehicle/VehicleDetail";
import NeedsCheckScreen from "@/components/vehicle/NeedsCheckScreen";
import DownPaymentScreen from "@/components/vehicle/DownPaymentScreen";
import FinancingScreen from "@/components/vehicle/FinancingScreen";
import HiddenCostsScreen from "@/components/vehicle/HiddenCostsScreen";
import AffordabilityScreen from "@/components/vehicle/AffordabilityScreen";
import NegotiationScreen from "@/components/vehicle/NegotiationScreen";
import FinalSummaryScreen from "@/components/vehicle/FinalSummaryScreen";
import CompletionScreen from "@/components/vehicle/CompletionScreen";
import VehicleEducationalPopup from "@/components/vehicle/VehicleEducationalPopup";
import RepairEventScreen from "@/components/vehicle/RepairEventScreen";
import DecisionConsequenceStrip from "@/components/vehicle/DecisionConsequenceStrip";
import SimulatorGate from "@/components/modules/SimulatorGate";
import LunimarScene from "@/components/lunimar/LunimarScene";
import LunimarModuleShell from "@/components/lunimar/LunimarModuleShell";
import { useEffect } from "react";

const POPUPS: Record<string, { title: string; body: string }> = {
  welcome: {
    title: "Vehicle Ownership Is More Than a Monthly Payment",
    body: "When you buy a vehicle, you're committing to:\n\n• A down payment from your savings\n• Monthly loan payments\n• Insurance premiums\n• Fuel and maintenance costs\n• Depreciation over time\n\nA smart vehicle purchase supports your life without straining your finances.",
  },
  down_payment: {
    title: "What Does a Down Payment Do?",
    body: "A larger down payment reduces how much you borrow.\n\n• Less borrowed = lower monthly payments\n• Less borrowed = less total interest paid\n• But using too much cash can leave you vulnerable\n\nBalance is key — protect your emergency fund while reducing your loan.",
  },
  financing: {
    title: "APR and Loan Length Matter More Than You Think",
    body: "APR (Annual Percentage Rate) is the cost of borrowing money.\n\n• Lower APR = less money paid in interest\n• Shorter loans = higher monthly payments but much less total interest\n• Longer loans = lower monthly payments but more total cost\n\nA lower monthly payment can feel safer, but often means paying much more overall.",
  },
  hidden_costs: {
    title: "Total Cost of Ownership",
    body: "The sticker price is just the beginning.\n\nOwning a vehicle also costs:\n• Sales tax\n• Registration and title fees\n• Dealer fees\n• Insurance every month\n• Fuel every month\n• Maintenance and repairs\n\nAlways look at what you'll actually spend, not just the purchase price.",
  },
  affordability: {
    title: "Fitting a Budget vs Being Affordable",
    body: "A car payment that fits your monthly budget can still be a poor financial decision.\n\nTrue affordability means:\n• You can handle the payment comfortably\n• You still have emergency savings\n• Your debt-to-income ratio stays healthy\n• You have cash flow left for life",
  },
  negotiation: {
    title: "Negotiation Is Part of the Process",
    body: "Most vehicle prices have some room for negotiation.\n\n• Do your research before negotiating\n• Be willing to walk away\n• Focus on the total price, not just the monthly payment\n• Dealer fees and add-ons are often negotiable\n\nEven a small discount can save you hundreds in interest over time.",
  },
};

const VehicleSimContent = () => {
  const sim = useVehicleSim();
  const navigate = useNavigate();

  // Lazily build the repair event the first time we enter the screen.
  // Must run before any early return to keep hook order stable.
  useEffect(() => {
    if (sim.screen === 'repair_event') sim.ensureRepairEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sim.screen]);

  if (sim.loading) return <div className="flex min-h-screen items-center justify-center bg-background"><p className="text-muted-foreground">Loading simulation...</p></div>;

  const handlePopupClose = () => {
    const key = sim.popupKey;
    sim.setPopupKey(null);
    switch (key) {
      case 'welcome': break;
      case 'down_payment':
        sim.commitDownPayment();
        sim.goTo('financing');
        break;
      case 'financing':
        sim.commitLoanTerm();
        sim.goTo('hidden_costs');
        break;
      case 'hidden_costs': sim.goTo('affordability'); break;
      case 'affordability': sim.goTo('negotiation'); break;
      case 'negotiation':
        sim.commitNegotiation();
        sim.goTo('repair_event');
        break;
    }
  };

  const latestDecision = sim.decisionLog.length > 0
    ? sim.decisionLog[sim.decisionLog.length - 1]
    : null;

  const STEP_MAP: Record<string, number> = {
    marketplace: 1, detail: 2, needs_check: 3, down_payment: 4, financing: 5,
    hidden_costs: 6, affordability: 7, negotiation: 8, repair_event: 9, final_summary: 10,
  };
  const stepIndex = STEP_MAP[sim.screen] ?? 0;
  const TOTAL_STEPS = 10;

  const renderScreen = () => {
    switch (sim.screen) {
      case 'welcome':
        return <WelcomeScreen profile={sim.profile} onStart={() => { sim.setPopupKey('welcome'); sim.startSim(); }} />;
      case 'marketplace':
        return <VehicleMarketplace vehicles={sim.vehicles} selectedKey={sim.selectedVehicleKey} onSelect={sim.setSelectedVehicleKey} onContinue={() => sim.goTo('detail')} onBack={() => sim.goTo('welcome')} />;
      case 'detail':
        return sim.selectedVehicle ? <VehicleDetail vehicle={sim.selectedVehicle} vehicles={sim.vehicles} onContinue={() => sim.goTo('needs_check')} onBack={() => sim.goTo('marketplace')} /> : null;
      case 'needs_check':
        return <NeedsCheckScreen
          selectedReason={sim.purchaseReason}
          onSelect={sim.setPurchaseReason}
          onContinue={() => { sim.commitVehicleChoice(); sim.goTo('down_payment'); }}
          onBack={() => sim.goTo('detail')}
        />;
      case 'down_payment':
        return sim.selectedVehicle ? <DownPaymentScreen vehicle={sim.selectedVehicle} pct={sim.downPaymentPct} onPctChange={sim.setDownPaymentPct} downPayment={sim.downPaymentAmount} loanAmount={sim.loanAmount} liquidBefore={sim.liquidBefore} emergencyFund={sim.profile.emergencyFund} onContinue={() => sim.setPopupKey('down_payment')} onBack={() => sim.goTo('needs_check')} /> : null;
      case 'financing':
        return <FinancingScreen loanAmount={sim.loanAmount} creditScore={sim.profile.creditScore} aprMap={sim.aprMap} selectedTerm={sim.loanTermMonths} onTermChange={sim.setLoanTermMonths} onContinue={() => sim.setPopupKey('financing')} onBack={() => sim.goTo('down_payment')} />;
      case 'hidden_costs':
        return sim.selectedVehicle ? <HiddenCostsScreen vehicle={sim.selectedVehicle} effectivePrice={sim.effectivePrice} downPayment={sim.downPaymentAmount} hiddenCosts={sim.hiddenCosts} upfrontCash={sim.upfrontCash} monthlyPayment={sim.monthlyPayment} totalMonthlyCost={sim.totalMonthlyCost} firstYearCost={sim.firstYearCost} onContinue={() => sim.setPopupKey('hidden_costs')} onBack={() => sim.goTo('financing')} /> : null;
      case 'affordability':
        return <AffordabilityScreen profile={sim.profile} totalMonthlyCost={sim.totalMonthlyCost} monthlyRemaining={sim.monthlyRemaining} liquidAfter={sim.liquidAfter} dti={sim.dti} rating={sim.affordabilityRating || 'overextended'} reasonMismatch={sim.reasonMismatch} onProceed={() => sim.setPopupKey('affordability')} onExit={sim.exitWithoutPurchase} />;
      case 'negotiation':
        return sim.selectedVehicle ? <NegotiationScreen vehicle={sim.selectedVehicle} currentDiscount={sim.negotiationDiscount} onNegotiate={sim.attemptNegotiation} onContinue={() => sim.setPopupKey('negotiation')} onBack={() => sim.goTo('affordability')} onChooseAnother={() => {}} /> : null;
      case 'repair_event':
        return sim.selectedVehicle ? (
          <RepairEventScreen
            repair={sim.repair}
            vehicleName={sim.selectedVehicle.vehicle_name}
            emergencyFund={sim.profile.emergencyFund}
            onChoose={(c) => { sim.commitRepair(c); sim.goTo('final_summary'); }}
          />
        ) : null;
      case 'final_summary':
        return sim.selectedVehicle ? <FinalSummaryScreen vehicleName={sim.selectedVehicle.vehicle_name} effectivePrice={sim.effectivePrice} downPayment={sim.downPaymentAmount} apr={sim.apr} loanTermMonths={sim.loanTermMonths} monthlyPayment={sim.monthlyPayment} insuranceMonthly={sim.selectedVehicle.insurance_monthly} maintenanceMonthly={sim.selectedVehicle.maintenance_monthly} fuelMonthly={sim.selectedVehicle.fuel_monthly} totalMonthlyCost={sim.totalMonthlyCost} firstYearCost={sim.firstYearCost} affordabilityRating={sim.affordabilityRating} purchaseGrade={sim.purchaseGrade} negotiationDiscount={sim.negotiationDiscount} onBuy={sim.completePurchase} onExit={sim.exitWithoutPurchase} processing={sim.processing} /> : null;
      case 'completed':
        return <CompletionScreen
          purchaseCompleted={sim.purchaseCompleted}
          vehicleName={sim.selectedVehicle?.vehicle_name}
          purchaseGrade={sim.purchaseGrade}
          affordabilityRating={sim.affordabilityRating}
          reasonMismatch={sim.reasonMismatch}
          decisionLog={sim.decisionLog}
          recap={sim.buildRecap(sim.purchaseCompleted)}
          onTryAgain={sim.tryAgain}
        />;
      default: return null;
    }
  };

  // Welcome and completed screens render full-bleed (no mission shell).
  const isShellScreen = sim.screen !== 'welcome' && sim.screen !== 'completed';

  return (
    <>
      {isShellScreen ? (
        <LunimarModuleShell
          realmKey="vehicle_ownership"
          title="Vehicle Ownership"
          stepIndex={stepIndex}
          totalSteps={TOTAL_STEPS}
          isLocked={false}
          lockMessage=""
          isCompleted={false}
          stepKey={sim.screen}
        >
          {latestDecision && (
            <div className="mb-4">
              <DecisionConsequenceStrip entry={latestDecision} />
            </div>
          )}
          {renderScreen()}
        </LunimarModuleShell>
      ) : (
        <div className="min-h-screen bg-background">
          <main className="container max-w-5xl mx-auto py-8 px-4">
            {renderScreen()}
          </main>
        </div>
      )}
      {sim.popupKey && POPUPS[sim.popupKey] && (
        <VehicleEducationalPopup
          open={!!sim.popupKey}
          title={POPUPS[sim.popupKey].title}
          body={POPUPS[sim.popupKey].body}
          onContinue={handlePopupClose}
        />
      )}
    </>
  );
};

const VehicleOwnershipSimulator = () => (
  <SimulatorGate moduleKey="vehicle_ownership">
    <VehicleSimContent />
  </SimulatorGate>
);

export default VehicleOwnershipSimulator;
